import { useTranslation } from 'react-i18next';

interface OrderItem {
  product_name: string;
  quantity: number;
  unit?: string;
  price?: number;
}

interface OrderSummaryCardProps {
  data: {
    order_id?: string;
    customer_name: string;
    items: OrderItem[];
    status?: string;
    total?: number;
    actions?: string[];
  };
  onSelect: (selection: Record<string, unknown>) => void;
}

export function OrderSummaryCard({ data, onSelect }: OrderSummaryCardProps) {
  const { t } = useTranslation();

  const statusColors: Record<string, string> = {
    detected: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-purple-100 text-purple-700',
    fulfilled: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
        <div>
          <span className="text-xs text-gray-500">
            {t('agentChat.cards.order', 'Order')}
            {data.order_id && ` #${data.order_id.slice(0, 8)}`}
          </span>
          <p className="text-sm font-medium text-gray-900">{data.customer_name}</p>
        </div>
        {data.status && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[data.status] || 'bg-gray-100 text-gray-600'}`}>
            {data.status}
          </span>
        )}
      </div>

      <div className="px-4 py-2">
        <table className="w-full text-sm">
          <tbody>
            {data.items.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-100 last:border-0">
                <td className="py-1.5 text-gray-700">{item.product_name}</td>
                <td className="py-1.5 text-right text-gray-600">
                  {item.quantity} {item.unit || ''}
                </td>
                {item.price != null && (
                  <td className="py-1.5 text-right font-medium w-24">{item.price.toFixed(2)}₪</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.total != null && (
        <div className="px-4 py-2 bg-gray-50 border-t flex justify-between text-sm font-bold">
          <span>{t('agentChat.cards.grandTotal', 'Total')}</span>
          <span>{data.total.toFixed(2)}₪</span>
        </div>
      )}

      {data.actions && data.actions.length > 0 && (
        <div className="px-4 py-2 border-t flex gap-2">
          {data.actions.includes('create_invoice') && (
            <button
              onClick={() => onSelect({ action: 'create_invoice' })}
              className="flex-1 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700"
            >
              🧾 {t('agentChat.cards.createInvoice', 'Create Invoice')}
            </button>
          )}
          {data.actions.includes('edit') && (
            <button
              onClick={() => onSelect({ action: 'edit' })}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300"
            >
              ✏️ {t('common.edit', 'Edit')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
